import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import apiClient from './apiClient';

/**
 * Sign in with Windows Hello / a passkey (REQ #21). Runs the WebAuthn
 * authentication ceremony and returns { user, token } like password login.
 * @param {string} email
 */
export const webauthnLogin = async (email) => {
  try {
    const opt = await apiClient.post('/auth/webauthn/login-options', { email });
    const { options, flowToken } = opt.data.data;
    const assertion = await startAuthentication({ optionsJSON: options });
    const verify = await apiClient.post('/auth/webauthn/login-verify', { flowToken, response: assertion });
    const { user, token } = verify.data.data;
    return { user, token };
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Enroll the current device as a Windows Hello / passkey for the signed-in user.
 * @param {string} [deviceName]
 */
export const webauthnRegister = async (deviceName) => {
  try {
    const opt = await apiClient.post('/auth/webauthn/register-options', {});
    const { options, flowToken } = opt.data.data;
    const attestation = await startRegistration({ optionsJSON: options });
    const verify = await apiClient.post('/auth/webauthn/register-verify', { flowToken, response: attestation, deviceName });
    return verify.data;
  } catch (error) {
    throw handleError(error);
  }
};

export const webauthnListCredentials = async () => {
  try {
    const response = await apiClient.get('/auth/webauthn/credentials');
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

export const webauthnDeleteCredential = async (id) => {
  try {
    const response = await apiClient.delete(`/auth/webauthn/credentials/${id}`);
    // Removing an authentication credential rotates the backend's session
    // epoch. Preserve the freshly bound token for this verified session so the
    // follow-up credential-list request succeeds; every other open session is
    // intentionally revoked.
    const replacementToken = response.data?.data?.token;
    if (replacementToken) localStorage.setItem('token', replacementToken);
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Login user with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise} - Promise containing user data and token
 */
export const login = async (email, password) => {
  try {
    const response = await apiClient.post('/auth/login', { email, password });
    
    // Check the structure of the response
    if (response.data && response.data.data) {
      const { user, token } = response.data.data;
      return { user, token };
    } else if (response.data && response.data.user && response.data.token) {
      return response.data;
    } else {
      throw new Error('Invalid response format from server');
    }
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Passwordless sign-in with the personal login link from the welcome mail
 * (employee/customer users). Returns { user, token } like password login.
 * @param {string} linkToken - the opaque token from the ?token= query param
 */
export const linkLogin = async (linkToken) => {
  return exchangeLoginLink('/auth/link-login', linkToken);
};

// The first exchange only establishes the HttpOnly browser cookie. Retrying
// once lets the server bind a browser only after it demonstrates cookie support.
const exchangeLoginLink = async (endpoint, linkToken) => {
  return withBrowserLoginLock(() => exchangeLoginLinkRequest(endpoint, linkToken));
};

// Serialize cookie setup across tabs where Web Locks is available. Otherwise
// serialize in this tab. Two simultaneous first-use responses must not replace
// one another's device cookies and spend two places on one browser.
let browserLoginQueue = Promise.resolve();
const withBrowserLoginLock = (operation) => {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 25000);
    return navigator.locks.request('tempus-link-device-login', { signal: controller.signal }, () => {
      clearTimeout(timeout);
      return operation();
    }).finally(() => clearTimeout(timeout));
  }
  const result = browserLoginQueue.then(operation, operation);
  browserLoginQueue = result.catch(() => {});
  return result;
};

const exchangeLoginLinkRequest = async (endpoint, linkToken) => {
  try {
    let response;
    try {
      response = await apiClient.post(endpoint, { token: linkToken }, { withCredentials: true });
    } catch (error) {
      if (error.response?.status !== 409 || error.response?.data?.error?.code !== 'LINK_DEVICE_SETUP_REQUIRED') throw error;
      response = await apiClient.post(endpoint, { token: linkToken }, { withCredentials: true });
    }
    const { user, token } = response.data.data;
    return { user, token };
  } catch (error) {
    throw handleError(error);
  }
};

export const verifyLoginLink = (token) => exchangeLoginLink('/auth/verify-login-link', token);

export const requestLoginLink = async (email) => {
  try {
    const response = await apiClient.post('/auth/request-login-link', { email });
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Get current user from token
 * @returns {Promise} - Promise containing user data
 */
export const getCurrentUser = async () => {
  try {
    const response = await apiClient.get('/auth/me');
    
    // Check the structure of the response
    if (response.data && response.data.data) {
      return response.data.data;
    } else if (response.data) {
      return response.data;
    } else {
      throw new Error('Invalid response format from server');
    }
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Logout user
 * @returns {Promise} - Empty promise
 */
export const logout = async (token) => {
  try {
    const response = await apiClient.post('/auth/logout', undefined, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
    return response.data;
  } catch (error) {
    // Even if logout fails on the server, we'll clear local storage
    throw handleError(error);
  }
};

export const oidcConfig = async () => (await apiClient.get('/auth/oidc/config')).data.data;
export const oidcStartUrl = () => `${String(apiClient.defaults.baseURL).replace(/\/$/, '')}/auth/oidc/start`;
export const oidcComplete = async () => (await apiClient.post('/auth/oidc/complete', {}, { withCredentials: true })).data.data;
export const selectStaffMode = async (mode) => (await apiClient.post('/auth/staff-mode', { mode })).data.data;
export const revokeIssuedToken = async (token) => (await apiClient.post('/auth/revoke-issued-token', undefined,
  { headers: { Authorization: `Bearer ${token}` } })).data;

/**
 * Request password reset
 * @param {string} email - User email
 * @returns {Promise} - Promise containing success message
 */
export const requestPasswordReset = async (email) => {
  try {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Reset password
 * @param {string} token - Reset token
 * @param {string} newPassword - New password
 * @returns {Promise} - Promise containing success message
 */
export const resetPassword = async (token, newPassword) => {
  try {
    const response = await apiClient.post('/auth/reset-password', { token, newPassword });
    return response.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Set password for first-time login
 * @param {string} newPassword - New password
 * @returns {Promise} - Promise containing user data and token
 */
export const setFirstTimePassword = async (newPassword) => {
  try {
    const response = await apiClient.post('/auth/set-first-password', { newPassword });

    // Check the structure of the response
    if (response.data && response.data.data) {
      const { user, token } = response.data.data;
      
      return { user, token };
    } else if (response.data && response.data.user && response.data.token) {
      return response.data;
    } else {
      throw new Error('Invalid response format from server');
    }
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Which sign-in methods the admin login page should offer for an e-mail.
 * @param {string} email
 * @returns {Promise<{password:boolean, webauthn:boolean}>}
 */
export const loginMethods = async (email) => {
  try {
    const response = await apiClient.post('/auth/login-methods', { email });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Helper function to handle errors
 * @param {object} error - Error object
 * @returns {Error} - Formatted error
 */
const handleError = (error) => {
  const message =
    error.response?.data?.error?.message ||
    error.response?.data?.message ||
    error.message;
  if (message) {
    const err = new Error(message);
    // Preserve the backend error code so pages can branch (e.g. the link-login
    // page shows a specific message for LINK_DEVICE_LIMIT).
    err.code = error.response?.data?.error?.code;
    err.status = error.response?.status;
    return err;
  }
  return error;
};
